import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Icon,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack,
  Paper,
  styled,
  alpha,
  Grid,
} from "@mui/material";
import dayjs from "dayjs";
import { IESDoc } from "../../../../../app.interface";
import { styled as muiStyled } from "@mui/material/styles";

// Theme colors to match POSSellableItemDetailsDrawer
const THEME_COLORS = {
  primary: '#0891B2',
  primaryHover: '#0E7490',
  secondary: '#64748B',
  danger: '#DC2626',
  dangerHover: '#B91C1C',
  success: '#059669',
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B',
    secondary: '#64748B',
    light: '#94A3B8',
  }
};

const StyledCard = muiStyled(Paper)(({ theme }) => ({
  backgroundColor: THEME_COLORS.background.card,
  borderRadius: 12,
  border: `1px solid ${THEME_COLORS.border.light}`,
  marginBottom: theme.spacing(3),
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
}));

const SectionTitle = muiStyled(Typography)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: THEME_COLORS.primary,
  fontFamily: 'Montserrat',
  fontWeight: 600,
  marginBottom: 16,
  '& .MuiIcon-root': {
    color: THEME_COLORS.primary,
    fontSize: 20,
  },
});

// Update allergy chip style for better text padding
const AllergyChip = muiStyled(Chip)(() => ({
  backgroundColor: '#FFFBEB',
  color: '#F59E42',
  border: '1px solid #FBBF24',
  fontWeight: 600,
  borderRadius: 8,
  fontSize: '1em',
  marginRight: 8,
  marginBottom: 8,
  paddingLeft: 12,
  paddingRight: 12,
  height: 32,
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
  },
}));

// Update status chip style to use BOM green (#059669)
const StatusChip = muiStyled(Chip)<{ filled?: boolean }>(({ filled }) => ({
  backgroundColor: filled ? '#059669' : 'transparent',
  color: filled ? '#fff' : '#059669',
  border: `1px solid #059669`,
  fontWeight: 600,
  borderRadius: 8,
  fontSize: '1em',
  paddingLeft: 12,
  paddingRight: 12,
  height: 32,
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
  },
  '& .MuiChip-icon': {
    color: filled ? '#fff' : '#059669',
  },
}));

// Add styled sellable item card
const SellableItemCard = muiStyled(Box)(({ theme }) => ({
  background: THEME_COLORS.background.light,
  border: `1px solid ${THEME_COLORS.border.light}`,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '20px 24px',
  marginBottom: 20,
  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
  transition: 'box-shadow 0.2s',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(8,145,178,0.08)',
  },
}));

interface MenuItemDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  item: IESDoc;
}

export const MenuItemDetailsDrawer: React.FC<MenuItemDetailsDrawerProps> = ({
  open,
  onClose,
  item,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "40%",
          minWidth: 400,
          maxWidth: 600,
          p: 0,
          backgroundImage: "none",
          bgcolor: THEME_COLORS.background.light,
          backdropFilter: "blur(10px)",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: THEME_COLORS.background.main,
            borderBottom: `1px solid ${THEME_COLORS.border.light}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon sx={{ color: THEME_COLORS.primary }}>restaurant_menu</Icon>
            <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 600, color: THEME_COLORS.text.primary }}>
              {item.name || "Menu Item Details"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Stack spacing={3}>
            {/* Basic Info */}
            <StyledCard>
              <Box sx={{ p: 3 }}>
                <SectionTitle variant="subtitle1">
                  <Icon>info</Icon>
                  Basic Information
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>
                      {item.description || "No description provided"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Price
                    </Typography>
                    <Typography variant="h6" sx={{ color: THEME_COLORS.primary, fontWeight: 700 }}>
                      BHD {item.price?.toFixed(3)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Total Cost
                    </Typography>
                    <Typography variant="h6" sx={{ color: THEME_COLORS.primary, fontWeight: 700 }}>
                      BHD {item.total_cost?.toFixed(3)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Category
                    </Typography>
                    <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary }}>
                      {item.category || "Uncategorized"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Preparation Time
                    </Typography>
                    <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary }}>
                      {item.expected_prep_duration || "Not specified"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Allergies
                    </Typography>
                    {item.allergies?.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {item.allergies.map((allergy: any) => (
                          <AllergyChip
                            key={allergy}
                            label={allergy}
                            size="small"
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body1">No allergies listed</Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </StyledCard>

            {/* Sellable Items */}
            <StyledCard>
              <Box sx={{ p: 3 }}>
                <SectionTitle variant="subtitle1">
                  <Icon>inventory</Icon>
                  Sellable Items
                </SectionTitle>
                {item.sellable_items?.length > 0 ? (
                  <Box>
                    {item.sellable_items.map((sellableItem: any) => (
                      <SellableItemCard key={sellableItem._id}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#E0F2FE',
                            color: THEME_COLORS.primary,
                            mr: 2,
                          }}
                        >
                          <Icon sx={{ fontSize: 32 }}>inventory_2</Icon>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              color: THEME_COLORS.text.primary,
                              fontWeight: 600,
                              fontFamily: 'Montserrat',
                              fontSize: '1.08em',
                              mb: 0.5,
                            }}
                          >
                            {sellableItem.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: THEME_COLORS.text.secondary,
                              fontWeight: 400,
                              fontFamily: 'Montserrat',
                            }}
                          >
                            Quantity: {sellableItem.quantity}
                          </Typography>
                        </Box>
                      </SellableItemCard>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1" color={THEME_COLORS.text.secondary}>
                    No sellable items associated
                  </Typography>
                )}
              </Box>
            </StyledCard>

            {/* Status Info */}
            <StyledCard>
              <Box sx={{ p: 3 }}>
                <SectionTitle variant="subtitle1">
                  <Icon>info</Icon>
                  Status Information
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Availability
                    </Typography>
                    <StatusChip
                      size="small"
                      filled={item.is_available}
                      label={item.is_available ? "Available" : "Unavailable"}
                      icon={
                        <Icon fontSize="small">
                          {item.is_available ? "check_circle" : "cancel"}
                        </Icon>
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Status
                    </Typography>
                    <StatusChip
                      size="small"
                      filled={item.is_active}
                      label={item.is_active ? "Active" : "Inactive"}
                      variant={item.is_active ? undefined : "outlined"}
                      icon={
                        <Icon fontSize="small">
                          {item.is_active ? "check_circle" : "cancel"}
                        </Icon>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={THEME_COLORS.text.secondary} gutterBottom>
                      Created
                    </Typography>
                    <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary }}>
                      {dayjs(item.created_at).format("MMM D, YYYY h:mm A")} by {item.created_by_email}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </StyledCard>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
